import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Info, RefreshCcw } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { downloadAssessmentSnapshotPdf } from "../utils/pdfExport";
import { getAuditSnapshotPayload, readAuditSnapshotIndex } from "../lib/auditHistory";
import { hydrateServerPaidAssessments } from "../lib/paidAssessmentQuota";
import { convertMoney, getCachedFxRates, guessCurrencySymbol } from "../lib/fxRates";
import { computeGovernanceAdjustedExposure } from "../lib/governanceAdjustedExposure.js";
import useEntitlement from "../hooks/useEntitlement";

const ACTIVE_BIZ_KEY = "aifine_active_business_id_v1";
const DISPLAY_CURRENCY_KEY = "aifine_reports_display_currency_v2";
const PRIORITY_DISPLAY_CURRENCIES = ["GBP", "EUR", "USD"];
const MAX_VISIBLE_REGULATORY_PILLS = 4;
const US_STATE_FALLBACK_DISCLOSURE =
  "State-specific statutory exposure data is not currently configured for this jurisdiction. A conservative planning estimate has been used for modelling purposes. This figure should not be interpreted as a statutory maximum or legal opinion.";

const US_STATE_NAMES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function clamp(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function formatMoney(value, symbol = "$") {
  const n = safeNumber(value);
  if (n == null) return "—";
  return `${symbol}${Math.round(n).toLocaleString("en-US")}`;
}

function formatMoneyCompact(value, symbol = "$") {
  const n = safeNumber(value);
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${symbol}${(n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 1 : 2)}B`;
  if (abs >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`;
  if (abs >= 1_000) return `${symbol}${Math.round(n / 1_000)}k`;
  return `${symbol}${Math.round(n).toLocaleString("en-US")}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const dt = new Date(String(iso));
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString();
}

function uniqueStrings(values = []) {
  return Array.from(new Set(safeArray(values).map((value) => String(value || "").trim()).filter(Boolean)));
}

function normalizeFxCache(anyFx) {
  if (!isRecord(anyFx)) return null;
  if (!isRecord(anyFx?.rates) || !anyFx?.base) return null;

  const rates = {};
  Object.keys(anyFx.rates || {}).forEach((code) => {
    const currency = String(code || "").trim().toUpperCase();
    const rate = Number(anyFx.rates[code]);
    if (currency && Number.isFinite(rate) && rate > 0) rates[currency] = rate;
  });

  if (!Object.keys(rates).length) return null;

  return {
    version: anyFx?.version || anyFx?.cache_version || "fx.normalized",
    source_label: anyFx?.source_label || "FX source",
    retrieved_at_iso: anyFx?.retrieved_at_iso || null,
    as_of_iso: anyFx?.as_of_iso || null,
    base: String(anyFx.base || "EUR").toUpperCase(),
    rates,
  };
}

function normalizeExposure(exposure) {
  if (!isRecord(exposure)) return null;

  const headline = isRecord(exposure?.headline) ? exposure.headline : null;
  const byCurrency = isRecord(exposure?.combined_by_currency) ? exposure.combined_by_currency : null;
  if (byCurrency && Object.keys(byCurrency).length) return exposure;

  const code = String(headline?.currency_code || "").trim().toUpperCase();
  if (!code) return exposure;

  return {
    ...exposure,
    combined_by_currency: {
      [code]: {
        combined_structured_worst_case_native: headline?.combined_structured_worst_case_native ?? null,
        combined_net_native:
          headline?.combined_net_native ?? headline?.combined_net_planning_case_native ?? null,
        combined_floor_native: headline?.combined_floor_native ?? null,
        currency_code: code,
        currency_symbol: headline?.currency_symbol ?? guessCurrencySymbol(code),
      },
    },
  };
}

function extractAuditFxCache(payload) {
  const source = isRecord(payload) ? payload : {};
  const candidates = [
    source?.fx_cache,
    source?.fxCache,
    source?.fx,
    source?.meta?.fx_cache,
    source?.meta?.fxCache,
    source?.summary?.fx_cache,
    source?.summary?.fxCache,
    source?.risk_exposure?.fx_cache,
    source?.risk_exposure?.fxCache,
    source?.risk_exposure?.fx,
    source?.risk_exposure?.fx_rates_cache,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeFxCache(candidate);
    if (normalized) return normalized;
  }

  return null;
}

function getCalculationCurrency(exposure) {
  const headlineCode = String(exposure?.headline?.currency_code || "").trim().toUpperCase();
  if (headlineCode) return headlineCode;
  const buckets = isRecord(exposure?.combined_by_currency) ? exposure.combined_by_currency : {};
  const firstCode = Object.keys(buckets || {}).find(Boolean);
  return firstCode ? String(firstCode).toUpperCase() : "";
}

function getAvailableCurrencies(exposure) {
  const buckets = isRecord(exposure?.combined_by_currency) ? exposure.combined_by_currency : {};
  const codes = Object.keys(buckets || {}).map((code) => String(code).toUpperCase()).filter(Boolean);
  if (codes.length) return uniqueStrings(codes);
  const calcCurrency = getCalculationCurrency(exposure);
  return calcCurrency ? [calcCurrency] : [];
}

function resolveDisplayMetrics({ exposure, displayCurrencyCode, fxCache }) {
  const normalized = normalizeExposure(exposure);
  if (!normalized) {
    const code = String(displayCurrencyCode || "").toUpperCase();
    return {
      displayCurrencyCode: code,
      symbol: guessCurrencySymbol(code),
      high: null,
      expected: null,
      low: null,
      converted: false,
      missing: [],
    };
  }

  const buckets = isRecord(normalized?.combined_by_currency) ? normalized.combined_by_currency : {};
  const calcCurrency = getCalculationCurrency(normalized);
  const targetCode = String(displayCurrencyCode || calcCurrency || Object.keys(buckets || {})[0] || "").toUpperCase();
  const directBucket = buckets[targetCode];

  const canConvert =
    isRecord(fxCache?.rates) &&
    Object.keys(fxCache.rates || {}).length > 0 &&
    Object.keys(buckets || {}).length > 0 &&
    Boolean(targetCode);

  if (canConvert) {
    let high = 0;
    let expected = 0;
    let low = 0;
    const missing = new Set();

    Object.keys(buckets || {}).forEach((currencyCode) => {
      const bucket = buckets[currencyCode];
      if (!isRecord(bucket)) return;
      const fromCode = String(currencyCode || "").toUpperCase();
      const bucketHigh = safeNumber(bucket?.combined_structured_worst_case_native);
      const bucketExpected = safeNumber(bucket?.combined_net_native);
      const bucketLow = safeNumber(bucket?.combined_floor_native);

      const convertedHigh = bucketHigh != null ? convertMoney(bucketHigh, fromCode, targetCode, fxCache) : null;
      const convertedExpected = bucketExpected != null ? convertMoney(bucketExpected, fromCode, targetCode, fxCache) : null;
      const convertedLow = bucketLow != null ? convertMoney(bucketLow, fromCode, targetCode, fxCache) : null;

      if (bucketHigh != null && convertedHigh == null) missing.add(fromCode);
      if (bucketExpected != null && convertedExpected == null) missing.add(fromCode);
      if (bucketLow != null && convertedLow == null) missing.add(fromCode);

      if (typeof convertedHigh === "number") high += convertedHigh;
      if (typeof convertedExpected === "number") expected += convertedExpected;
      if (typeof convertedLow === "number") low += convertedLow;
    });

    return {
      displayCurrencyCode: targetCode,
      symbol: guessCurrencySymbol(targetCode),
      high: Math.round(high),
      expected: Math.round(expected),
      low: Math.round(low),
      converted: true,
      missing: Array.from(missing),
    };
  }

  const bucket = directBucket || buckets[calcCurrency] || null;
  if (bucket) {
    const activeCode = directBucket ? targetCode : calcCurrency;
    return {
      displayCurrencyCode: activeCode,
      symbol: bucket?.currency_symbol || guessCurrencySymbol(activeCode),
      high: safeNumber(bucket?.combined_structured_worst_case_native),
      expected: safeNumber(bucket?.combined_net_native),
      low: safeNumber(bucket?.combined_floor_native),
      converted: false,
      missing: [],
    };
  }

  const headline = isRecord(normalized?.headline) ? normalized.headline : {};
  const fallbackCode = String(calcCurrency || targetCode).toUpperCase();
  return {
    displayCurrencyCode: fallbackCode,
    symbol: headline?.currency_symbol || guessCurrencySymbol(fallbackCode),
    high: safeNumber(headline?.combined_structured_worst_case_native),
    expected: safeNumber(headline?.combined_net_native ?? headline?.combined_net_planning_case_native),
    low: safeNumber(headline?.combined_floor_native),
    converted: false,
    missing: [],
  };
}


function pickFirstRecord(...candidates) {
  for (const candidate of candidates) {
    if (isRecord(candidate)) return candidate;
  }
  return null;
}

function extractEvidenceSummaryForLayer2(payload) {
  const p = isRecord(payload) ? payload : {};
  return pickFirstRecord(
    p?.evidence_summary,
    p?.evidenceSummary,
    p?.summary?.evidence_summary,
    p?.summary?.evidenceSummary,
    p?.meta?.evidence_summary,
    p?.meta?.evidenceSummary,
  );
}

function extractRemediationSummaryForLayer2(payload) {
  const p = isRecord(payload) ? payload : {};
  return pickFirstRecord(
    p?.remediation_summary,
    p?.remediationSummary,
    p?.summary?.remediation_summary,
    p?.summary?.remediationSummary,
    p?.meta?.remediation_summary,
    p?.meta?.remediationSummary,
  );
}

function convertLayer2Money(value, fromCode, toCode, fxCache) {
  const amount = safeNumber(value);
  if (amount == null) return { value: null, converted: false, conversionMissing: false };

  const from = String(fromCode || "").toUpperCase();
  const to = String(toCode || from || "").toUpperCase();

  if (!from || !to || from === to) {
    return { value: Math.round(amount), converted: false, conversionMissing: false };
  }

  if (!fxCache) {
    return { value: Math.round(amount), converted: false, conversionMissing: true };
  }

  const converted = convertMoney(amount, from, to, fxCache);
  if (typeof converted === "number" && Number.isFinite(converted)) {
    return { value: Math.round(converted), converted: true, conversionMissing: false };
  }

  return { value: Math.round(amount), converted: false, conversionMissing: true };
}

function resolveLayer2DisplayMetrics({ governanceAdjusted, displayCurrencyCode, fxCache }) {
  const layer2 = isRecord(governanceAdjusted?.layer_2) ? governanceAdjusted.layer_2 : null;
  if (!layer2) {
    const code = String(displayCurrencyCode || "USD").toUpperCase();
    return {
      available: false,
      displayCurrencyCode: code,
      symbol: guessCurrencySymbol(code),
      low: null,
      expected: null,
      high: null,
      reduction: null,
      converted: false,
      conversionMissing: false,
    };
  }

  const nativeCode = String(layer2?.currency_code || governanceAdjusted?.layer_1?.currency_code || displayCurrencyCode || "USD").toUpperCase();
  const requestedCode = String(displayCurrencyCode || nativeCode).toUpperCase();

  const low = convertLayer2Money(layer2?.low_native, nativeCode, requestedCode, fxCache);
  const expected = convertLayer2Money(layer2?.midpoint_native, nativeCode, requestedCode, fxCache);
  const high = convertLayer2Money(layer2?.high_native, nativeCode, requestedCode, fxCache);
  const reduction = convertLayer2Money(layer2?.reduction_from_expected_native, nativeCode, requestedCode, fxCache);

  const conversionMissing = Boolean(low.conversionMissing || expected.conversionMissing || high.conversionMissing || reduction.conversionMissing);
  const converted = Boolean(low.converted || expected.converted || high.converted || reduction.converted);
  const activeCode = conversionMissing && !converted ? nativeCode : requestedCode;

  return {
    available: true,
    displayCurrencyCode: activeCode,
    symbol: guessCurrencySymbol(activeCode),
    low: low.value,
    expected: expected.value,
    high: high.value,
    reduction: reduction.value,
    converted,
    conversionMissing,
    nativeCurrencyCode: nativeCode,
    requestedCurrencyCode: requestedCode,
  };
}


function resolveLayer3DisplayMetrics({ governanceAdjusted, displayCurrencyCode, fxCache }) {
  const layer3 = isRecord(governanceAdjusted?.layer_3) ? governanceAdjusted.layer_3 : null;
  const layer2 = isRecord(governanceAdjusted?.layer_2) ? governanceAdjusted.layer_2 : null;
  const layer1 = isRecord(governanceAdjusted?.layer_1) ? governanceAdjusted.layer_1 : null;
  const nativeCode = String(layer3?.currency_code || layer2?.currency_code || layer1?.currency_code || displayCurrencyCode || "USD").toUpperCase();
  const requestedCode = String(displayCurrencyCode || nativeCode).toUpperCase();

  if (!layer3) {
    return {
      available: false,
      displayCurrencyCode: requestedCode,
      symbol: guessCurrencySymbol(requestedCode),
      exposureReduction: null,
      remainingUnresolved: null,
      totalModeled: null,
      converted: false,
      conversionMissing: false,
    };
  }

  const exposureReduction = convertLayer2Money(layer3?.exposure_reduction_through_remediation_native, nativeCode, requestedCode, fxCache);
  const remainingUnresolved = convertLayer2Money(layer3?.remaining_unresolved_exposure_native, nativeCode, requestedCode, fxCache);
  const totalModeled = convertLayer2Money(layer3?.total_modeled_finding_exposure_native, nativeCode, requestedCode, fxCache);
  const conversionMissing = Boolean(exposureReduction.conversionMissing || remainingUnresolved.conversionMissing || totalModeled.conversionMissing);
  const converted = Boolean(exposureReduction.converted || remainingUnresolved.converted || totalModeled.converted);
  const activeCode = conversionMissing && !converted ? nativeCode : requestedCode;

  return {
    available: true,
    displayCurrencyCode: activeCode,
    symbol: guessCurrencySymbol(activeCode),
    exposureReduction: exposureReduction.value,
    remainingUnresolved: remainingUnresolved.value,
    totalModeled: totalModeled.value,
    converted,
    conversionMissing,
    nativeCurrencyCode: nativeCode,
    requestedCurrencyCode: requestedCode,
  };
}

function formatSignedMoney(value, symbol = "$") {
  const n = safeNumber(value);
  if (n == null) return "—";
  const prefix = n > 0 ? "−" : n < 0 ? "+" : "";
  return `${prefix}${formatMoney(Math.abs(n), symbol)}`;
}

function getRiskGrade(score, explicitGrade = "") {
  const explicit = String(explicitGrade || "").trim().toUpperCase();
  if (/^[A-E]$/.test(explicit)) return explicit;
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  if (n <= 15) return "A";
  if (n <= 30) return "B";
  if (n <= 50) return "C";
  if (n <= 75) return "D";
  return "E";
}

function getExecutiveSignal(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Unscored";
  if (n <= 20) return "Contained";
  if (n <= 40) return "Manageable";
  if (n <= 60) return "Elevated";
  if (n <= 80) return "Material";
  return "Critical";
}

function pickCounts(payload) {
  const assessment = isRecord(payload?.assessment) ? payload.assessment : {};
  return {
    failed:
      typeof assessment?.failed_count === "number"
        ? assessment.failed_count
        : Array.isArray(assessment?.failed_question_ids)
          ? assessment.failed_question_ids.length
          : 0,
    unsure:
      typeof assessment?.unsure_count === "number"
        ? assessment.unsure_count
        : Array.isArray(assessment?.unsure_question_ids)
          ? assessment.unsure_question_ids.length
          : 0,
    total:
      typeof assessment?.total_questions === "number"
        ? assessment.total_questions
        : Array.isArray(assessment?.active_question_ids)
          ? assessment.active_question_ids.length
          : 60,
  };
}

function resolvePrimaryRiskScore({ payload, meta }) {
  const assessment = isRecord(payload?.assessment) ? payload.assessment : {};
  const candidates = [
    assessment?.risk_score_pct,
    assessment?.severity_score_0_100,
    payload?.risk_score_pct,
    meta?.risk_score_pct,
    assessment?.compliance_score_pct,
    payload?.compliance_score_pct,
    meta?.compliance_score_pct,
  ];

  for (const candidate of candidates) {
    const n = safeNumber(candidate);
    if (n != null) return Math.round(clamp(n, 0, 100));
  }

  return null;
}

function safeGetActiveBusinessId() {
  try {
    return String(localStorage.getItem(ACTIVE_BIZ_KEY) || "");
  } catch {
    return "";
  }
}

function safeReadIndex({ businessId } = {}) {
  try {
    const rows = readAuditSnapshotIndex({ businessId: businessId || null });
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function safeGetSnapshotPayload(snapshotId) {
  try {
    return getAuditSnapshotPayload(snapshotId);
  } catch {
    return null;
  }
}

function readDisplayCurrencyPref() {
  try {
    return String(localStorage.getItem(DISPLAY_CURRENCY_KEY) || "").toUpperCase();
  } catch {
    return "";
  }
}

function writeDisplayCurrencyPref(code) {
  try {
    if (code) localStorage.setItem(DISPLAY_CURRENCY_KEY, String(code).toUpperCase());
  } catch {
    // ignore
  }
}

function buildRemediationContext({ snapshotId, payload, meta }) {
  const sid = snapshotId == null ? "" : String(snapshotId);
  const p = isRecord(payload) ? payload : {};
  const m = isRecord(meta) ? meta : {};
  const businessId =
    p?.business_id ||
    p?.config?.business_id ||
    p?.meta?.business_id ||
    m?.business_id ||
    "";

  const createdAt =
    p?.created_at_iso ||
    p?.generated_at ||
    p?.meta?.created_at_iso ||
    m?.created_at_iso ||
    "";

  return {
    snapshot_id: sid,
    business_id: businessId ? String(businessId) : "",
    entity_name: p?.entity_name || m?.entity_name || "",
    created_at_iso: createdAt ? String(createdAt) : "",
    source: "reports",
    written_at_iso: new Date().toISOString(),
  };
}

function safeWriteRemediationContext(context) {
  try {
    localStorage.setItem("aifine_remediation_context_v1", JSON.stringify(context));
  } catch {
    // ignore
  }
}

function normalizeStateCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function normalizeSelectedStates(values = []) {
  return uniqueStrings(safeArray(values).map(normalizeStateCode).filter(Boolean));
}

function extractSelectedStates(payload, meta) {
  const candidates = [
    payload?.selectedStates,
    payload?.selected_states,
    payload?.summary?.selectedStates,
    payload?.summary?.selected_states,
    payload?.config?.selectedStates,
    payload?.config?.selected_states,
    payload?.meta?.selectedStates,
    payload?.meta?.selected_states,
    meta?.selectedStates,
    meta?.selected_states,
  ];

  for (const candidate of candidates) {
    const states = normalizeSelectedStates(candidate);
    if (states.length) return states;
  }

  return [];
}

function normalizeJurisdictionToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function isUsStatePlaceholder(value) {
  const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
  return token === "US STATE" || token === "US STATES" || token === "US (STATE)";
}

function isUsFederalToken(value) {
  const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
  return token === "US" || token === "US FED" || token === "US FEDERAL" || token === "US (FED)";
}

function getJurisdictionTokens(item, fallbackKey) {
  const values = [
    item?.jurisdiction,
    item?.jurisdiction_key,
    item?.jurisdiction_code,
    item?.region,
    item?.country,
    item?.state,
    item?.state_code,
    item?.stateCode,
    item?.display_name,
    item?.regime_name,
    item?.regime_key,
    fallbackKey,
    ...(safeArray(item?.jurisdictions)),
    ...(safeArray(item?.scope_jurisdictions)),
  ];

  return uniqueStrings(values);
}

function inferJurisdictionFromTokens({ tokens = [], currencyCode = "", scope = [] }) {
  const joined = tokens.map((token) => normalizeJurisdictionToken(token).replace(/[_-]/g, " ")).join(" | ");
  const selectedScope = uniqueStrings(scope.map((value) => normalizeJurisdictionToken(value).replace(/[_-]/g, " ")));

  const stateCode = tokens
    .map((token) => normalizeStateCode(token))
    .find((code) => Boolean(US_STATE_NAMES[code]));

  if (stateCode) {
    return {
      key: `STATE_${stateCode}`,
      label: US_STATE_NAMES[stateCode],
      currency: "USD",
      explicit: true,
      kind: "state",
    };
  }

  if (joined.includes("UNITED KINGDOM") || joined.includes(" UK ") || joined.startsWith("UK ") || joined.endsWith(" UK") || joined.includes("UK GDPR") || joined.includes("ICO")) {
    return { key: "UK", label: "UK", currency: "GBP", explicit: true, kind: "jurisdiction" };
  }

  if (joined.includes("EUROPEAN UNION") || joined.includes(" EU ") || joined.startsWith("EU ") || joined.endsWith(" EU") || joined.includes("EU AI ACT") || joined.includes("EEA") || joined.includes("GDPR")) {
    if (!joined.includes("UK GDPR") && !joined.includes("UNITED KINGDOM")) {
      return { key: "EU", label: "EU", currency: "EUR", explicit: true, kind: "jurisdiction" };
    }
  }

  const federalHints = ["FTC", "CFPB", "SEC", "DOJ", "FCC", "HHS", "HUD", "EEOC", "FERPA", "COPPA", "FEDERAL"];
  if (isUsFederalToken(joined) || federalHints.some((hint) => joined.includes(hint))) {
    return { key: "US_FEDERAL", label: "US Federal", currency: "USD", explicit: true, kind: "jurisdiction" };
  }

  if (isUsStatePlaceholder(joined)) {
    return { key: "US_STATES", label: "US States", currency: "USD", explicit: false, kind: "state-group" };
  }

  if (currencyCode === "GBP" && selectedScope.some((token) => token.includes("UK"))) {
    return { key: "UK", label: "UK", currency: "GBP", explicit: false, kind: "jurisdiction" };
  }
  if (currencyCode === "EUR" && selectedScope.some((token) => token.includes("EU"))) {
    return { key: "EU", label: "EU", currency: "EUR", explicit: false, kind: "jurisdiction" };
  }
  if (currencyCode === "USD" && selectedScope.some((token) => token.includes("US"))) {
    return { key: "US_FEDERAL", label: "US Federal", currency: "USD", explicit: false, kind: "jurisdiction" };
  }

  return null;
}

function emptyMetricBucket() {
  return {
    high: null,
    expected: null,
    low: null,
    present: false,
    hasHigh: false,
    hasExpected: false,
    hasLow: false,
    expectedSource: "missing",
  };
}

function firstFiniteNumber(candidates = []) {
  for (const candidate of candidates) {
    const n = safeNumber(candidate);
    if (n != null) return n;
  }
  return null;
}

function readNestedNumber(source, path = []) {
  let current = source;
  for (const key of path) {
    if (!isRecord(current) && !Array.isArray(current)) return null;
    current = current?.[key];
  }
  return safeNumber(current);
}

function clampExpectedRatio(value) {
  const n = safeNumber(value);
  if (n == null) return null;
  return clamp(n, 0, 1);
}

function deriveExpectedRatioFromTriplet(high, expected, low) {
  const hi = safeNumber(high);
  const ex = safeNumber(expected);
  const lo = safeNumber(low);
  if (hi == null || ex == null || lo == null || hi === lo) return null;
  return clampExpectedRatio((ex - lo) / (hi - lo));
}

function buildExpectedFallbackProfile(exposure) {
  const normalized = normalizeExposure(exposure);
  const buckets = isRecord(normalized?.combined_by_currency) ? normalized.combined_by_currency : {};
  const byCurrency = {};
  let defaultRatio = null;

  Object.entries(buckets).forEach(([currencyCode, bucket]) => {
    const ratio = deriveExpectedRatioFromTriplet(
      bucket?.combined_structured_worst_case_native,
      bucket?.combined_net_native,
      bucket?.combined_floor_native,
    );
    const code = String(currencyCode || "").toUpperCase();
    if (ratio != null && code) {
      byCurrency[code] = ratio;
      if (defaultRatio == null) defaultRatio = ratio;
    }
  });

  if (defaultRatio == null) {
    defaultRatio = deriveExpectedRatioFromTriplet(
      normalized?.headline?.combined_structured_worst_case_native,
      normalized?.headline?.combined_net_native ?? normalized?.headline?.combined_net_planning_case_native,
      normalized?.headline?.combined_floor_native,
    );
  }

  return {
    byCurrency,
    defaultRatio: defaultRatio != null ? defaultRatio : 0.5,
  };
}

function deriveExpectedFallbackValue({ high, low, currencyCode, expectedProfile }) {
  const hi = safeNumber(high);
  const lo = safeNumber(low);
  if (hi == null || lo == null) return null;
  const code = String(currencyCode || "").toUpperCase();
  const ratio = clampExpectedRatio(expectedProfile?.byCurrency?.[code]) ?? clampExpectedRatio(expectedProfile?.defaultRatio) ?? 0.5;
  return Math.round(lo + (hi - lo) * ratio);
}

function resolveRegimeExpectedValue(entry, { high, low, currencyCode, expectedProfile } = {}) {
  const direct = firstFiniteNumber([
    entry?.net_exposure_native,
    entry?.expected_exposure_native,
    entry?.planning_case_native,
    entry?.net_native,
    entry?.expected_native,
    entry?.mid_exposure_native,
    entry?.predicted_exposure_native,
    entry?.expected_amount_native,
    entry?.estimated_exposure_native,
    entry?.native_expected,
    entry?.native_net_exposure,
    readNestedNumber(entry, ["expected", "native"]),
    readNestedNumber(entry, ["expected_exposure", "native"]),
    readNestedNumber(entry, ["planning_case", "native"]),
    readNestedNumber(entry, ["net_exposure", "native"]),
    readNestedNumber(entry, ["amounts", "expected"]),
    readNestedNumber(entry, ["amounts", "net"]),
    readNestedNumber(entry, ["metrics", "expected"]),
    readNestedNumber(entry, ["metrics", "net"]),
  ]);

  if (direct != null) {
    return { value: direct, source: "stored" };
  }

  const fallback = deriveExpectedFallbackValue({ high, low, currencyCode, expectedProfile });
  if (fallback != null) {
    return { value: fallback, source: "derived" };
  }

  return { value: null, source: "missing" };
}

function addMetricBucket(target, source) {
  const next = { ...target };
  const high = safeNumber(source?.high);
  const expected = safeNumber(source?.expected);
  const low = safeNumber(source?.low);

  if (high != null) {
    next.high = (safeNumber(next.high) ?? 0) + high;
    next.hasHigh = true;
    next.present = true;
  }
  if (expected != null) {
    next.expected = (safeNumber(next.expected) ?? 0) + expected;
    next.hasExpected = true;
    next.present = true;
    next.expectedSource = source?.expectedSource || "stored";
  }
  if (low != null) {
    next.low = (safeNumber(next.low) ?? 0) + low;
    next.hasLow = true;
    next.present = true;
  }

  return next;
}

function getUsStateDetailRows(perRegime) {
  const usState = isRecord(perRegime?.US_STATE) ? perRegime.US_STATE : null;
  const detail = isRecord(usState?.us_state_detail) ? usState.us_state_detail : {};
  const rows = safeArray(detail?.per_state || detail?.perState || usState?.state_breakdown);
  const byStateCode = new Map();

  rows.forEach((row) => {
    if (!isRecord(row)) return;
    const stateCode = normalizeStateCode(row?.state || row?.state_code || row?.stateCode || row?.code);
    if (!stateCode) return;
    byStateCode.set(stateCode, row);
  });

  return byStateCode;
}

function deriveJurisdictionBreakdown({ payload, meta }) {
  const exposure = normalizeExposure(payload?.risk_exposure || null);
  const perRegime = isRecord(exposure?.per_regime) ? exposure.per_regime : {};
  const expectedProfile = buildExpectedFallbackProfile(exposure);
  const scope = uniqueStrings(payload?.summary?.jurisdictions || meta?.scope || payload?.jurisdictions || []);
  const selectedStates = extractSelectedStates(payload, meta);
  const usStateDetailRows = getUsStateDetailRows(perRegime);

  const orderedBaseRows = [
    { key: "UK", label: "UK", currency: "GBP", kind: "jurisdiction" },
    { key: "EU", label: "EU", currency: "EUR", kind: "jurisdiction" },
    { key: "US_FEDERAL", label: "US Federal", currency: "USD", kind: "jurisdiction" },
  ];

  const groups = new Map();
  orderedBaseRows.forEach((row) => {
    groups.set(row.key, { ...row, metrics: emptyMetricBucket(), sourceCount: 0, explicit: false, inferredOnly: false, scopeOnly: false });
  });

  Object.entries(perRegime || {}).forEach(([key, item]) => {
    const value = isRecord(item) ? item : {};
    const currencyCode = String(value?.currency_code || "").toUpperCase();
    const tokens = getJurisdictionTokens(value, key);
    const jurisdiction = inferJurisdictionFromTokens({ tokens, currencyCode, scope });
    if (!jurisdiction) return;

    const current = groups.get(jurisdiction.key) || {
      ...jurisdiction,
      metrics: emptyMetricBucket(),
      sourceCount: 0,
      explicit: false,
      inferredOnly: false,
      scopeOnly: false,
    };

    const highValue = firstFiniteNumber([
      value?.structured_worst_case_native,
      value?.high_exposure_native,
      value?.worst_case_native,
      value?.exposure_high_native,
      readNestedNumber(value, ["high", "native"]),
      readNestedNumber(value, ["amounts", "high"]),
      readNestedNumber(value, ["metrics", "high"]),
    ]);
    const lowValue = firstFiniteNumber([
      value?.floor_native,
      value?.low_exposure_native,
      value?.minimum_exposure_native,
      value?.exposure_low_native,
      readNestedNumber(value, ["low", "native"]),
      readNestedNumber(value, ["amounts", "low"]),
      readNestedNumber(value, ["metrics", "low"]),
    ]);
    const resolvedExpected = resolveRegimeExpectedValue(value, {
      high: highValue,
      low: lowValue,
      currencyCode,
      expectedProfile,
    });

    current.metrics = addMetricBucket(current.metrics, {
      high: highValue,
      expected: resolvedExpected.value,
      expectedSource: resolvedExpected.source,
      low: lowValue,
    });
    current.sourceCount += 1;
    current.explicit = current.explicit || jurisdiction.explicit;
    current.inferredOnly = current.inferredOnly || !jurisdiction.explicit;
    current.currency = current.currency || currencyCode || jurisdiction.currency;
    groups.set(jurisdiction.key, current);
  });

  const stateRows = selectedStates.map((code) => {
    const key = `STATE_${code}`;
    const current = groups.get(key) || {
      key,
      label: US_STATE_NAMES[code] || code,
      currency: "USD",
      kind: "state",
      metrics: emptyMetricBucket(),
      sourceCount: 0,
      explicit: false,
      inferredOnly: false,
      scopeOnly: true,
    };

    const stateDetail = usStateDetailRows.get(code);
    if (stateDetail && !current.metrics.present) {
      current.metrics = addMetricBucket(current.metrics, {
        high: firstFiniteNumber([
          stateDetail?.worst_native,
          stateDetail?.structured_worst_case_native,
          stateDetail?.high_exposure_native,
          stateDetail?.exposure_high_native,
        ]),
        expected: firstFiniteNumber([
          stateDetail?.net_native,
          stateDetail?.expected_native,
          stateDetail?.expected_exposure_native,
          stateDetail?.planning_case_native,
        ]),
        low: firstFiniteNumber([
          stateDetail?.floor_native,
          stateDetail?.low_exposure_native,
          stateDetail?.minimum_exposure_native,
          stateDetail?.exposure_low_native,
        ]),
        expectedSource: "stored",
      });

      if (current.metrics.present) {
        current.sourceCount += 1;
        current.explicit = true;
        current.inferredOnly = false;
        current.currency = "USD";
      }

      const metadata = isRecord(stateDetail?.state_metadata) ? stateDetail.state_metadata : {};
      current.planningBasisLabel =
        stateDetail?.planning_basis_label ||
        metadata?.planning_basis_label ||
        "State exposure planning estimate";
      current.fallbackState = Boolean(stateDetail?.fallback_state || metadata?.fallback_state);
      current.fallbackDisclosure =
        stateDetail?.fallback_disclosure ||
        metadata?.fallback_disclosure ||
        (current.fallbackState ? US_STATE_FALLBACK_DISCLOSURE : "");
      current.legalBasis = metadata?.legal_basis || stateDetail?.legal_basis || "";
      current.statutoryPenaltyBasis = metadata?.statutory_penalty_basis || stateDetail?.statutory_penalty_basis || "";
      current.confidenceLevel = metadata?.confidence_level || stateDetail?.confidence_level || "";
    }

    current.scopeOnly = !current.metrics.present;
    groups.set(key, current);
    return current;
  });

  scope.forEach((value) => {
    const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
    if (token.includes("UK") && groups.has("UK")) groups.get("UK").scopeOnly = !groups.get("UK").metrics.present;
    if (token.includes("EU") && groups.has("EU")) groups.get("EU").scopeOnly = !groups.get("EU").metrics.present;
    if ((token.includes("US") || token.includes("FED")) && groups.has("US_FEDERAL")) {
      groups.get("US_FEDERAL").scopeOnly = !groups.get("US_FEDERAL").metrics.present;
    }
  });

  const rows = [
    ...orderedBaseRows.map((row) => groups.get(row.key)).filter(Boolean),
    ...stateRows.sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""))),
  ].filter((row) => row && (row.metrics.present || row.scopeOnly || row.sourceCount > 0));

  const explicitStateRows = rows.filter((row) => row.kind === "state");
  const hasSeparateStatesHeader = explicitStateRows.length > 0;

  return {
    rows,
    hasSeparateStatesHeader,
    note:
      rows.some((row) => row.scopeOnly)
        ? "Jurisdiction rows use stored native values. Snapshot rows without separate stored jurisdiction amounts remain scope-only."
        : "Jurisdiction rows use stored native values only.",
  };
}

function deriveRegulatorySources({ perRegime, displayCurrencyCode, fxCache, totalHighExposure }) {
  return Object.entries(isRecord(perRegime) ? perRegime : {})
    .map(([key, item]) => {
      const entry = isRecord(item) ? item : {};
      const nativeAmount = safeNumber(entry?.structured_worst_case_native);
      const nativeCode = String(entry?.currency_code || displayCurrencyCode || "").toUpperCase();
      let amount = null;

      if (nativeAmount != null) {
        if (!displayCurrencyCode || nativeCode === displayCurrencyCode) amount = nativeAmount;
        else if (fxCache) amount = convertMoney(nativeAmount, nativeCode, displayCurrencyCode, fxCache);
      }

      return {
        key,
        label: entry?.display_name || key,
        amount: safeNumber(amount),
      };
    })
    .filter((entry) => entry.amount != null && entry.amount > 0)
    .sort((a, b) => (b.amount || 0) - (a.amount || 0))
    .map((entry) => ({
      ...entry,
      pct:
        Number.isFinite(totalHighExposure) && totalHighExposure > 0
          ? Math.max(1, Math.round((entry.amount / totalHighExposure) * 100))
          : null,
    }));
}


function getRegimePillClasses(item) {
  const key = String(item?.key || item?.label || "").toUpperCase();
  if (key.includes("UK")) return "border-violet-300 bg-violet-100 text-violet-800 shadow-sm";
  if (key.includes("EU")) return "border-sky-300 bg-sky-100 text-sky-800 shadow-sm";
  if (key.includes("US_FEDERAL") || key.includes("FEDERAL")) return "border-amber-300 bg-amber-100 text-amber-800 shadow-sm";
  if (key.includes("US_") || key.includes("STATE")) return "border-emerald-300 bg-emerald-100 text-emerald-800 shadow-sm";
  return "border-slate-300 bg-slate-100 text-slate-800 shadow-sm";
}

function deriveExposureDrivers({ payload, counts, regimeSources, scorePct }) {
  const drivers = [];
  const labels = regimeSources.map((item) => String(item?.label || "").toLowerCase());
  const assessment = isRecord(payload?.assessment) ? payload.assessment : {};
  const gravityById = new Map(
    safeArray(assessment?.question_snapshot)
      .filter((question) => question?.id != null)
      .map((question) => [String(question.id), Number(question?.gravity || 0)]),
  );
  const unresolvedIds = uniqueStrings([
    ...safeArray(assessment?.failed_question_ids),
    ...safeArray(assessment?.unsure_question_ids),
  ]);
  const hasCriticalControlFinding = unresolvedIds.some(
    (id) => Number(gravityById.get(String(id)) || 0) >= 8,
  );

  if (labels.some((label) => label.includes("ai"))) drivers.push("High-risk AI classification exposure");
  if (labels.some((label) => label.includes("privacy") || label.includes("data") || label.includes("gdpr"))) {
    drivers.push("Data governance and privacy control gaps");
  }
  if (hasCriticalControlFinding) drivers.push("Critical control finding");
  if (counts.failed >= 20) drivers.push("Large number of failed controls");
  if (counts.unsure >= 10) drivers.push("Significant unresolved control uncertainty");
  if (safeArray(payload?.summary?.jurisdictions).length > 1) drivers.push("Multi-jurisdiction regulatory exposure");
  if (Number(scorePct) >= 60) drivers.push("Elevated weighted risk severity");

  return uniqueStrings(drivers).slice(0, 4);
}

function getTrendPoints({ rows, rowMetricsMap }) {
  return [...safeArray(rows)]
    .sort((a, b) => (Date.parse(a?.created_at_iso || "") || 0) - (Date.parse(b?.created_at_iso || "") || 0))
    .map((row, index) => {
      const id = String(row?.snapshot_id || index);
      const metrics = rowMetricsMap[id];
      const value = safeNumber(metrics?.high);
      if (value == null) return null;
      const date = new Date(row?.created_at_iso || "");
      return {
        id,
        value,
        label: Number.isNaN(date.getTime()) ? `#${index + 1}` : `${date.getMonth() + 1}/${date.getDate()}`,
      };
    })
    .filter(Boolean);
}

function useResizeObserver(ref) {
  const [rect, setRect] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref?.current;
    if (!node) return;

    const initial = node.getBoundingClientRect?.();
    if (initial) setRect({ width: Math.round(initial.width || 0), height: Math.round(initial.height || 0) });

    if (typeof ResizeObserver === "undefined") return;

    let raf = null;
    const observer = new ResizeObserver((entries) => {
      const box = entries?.[0]?.contentRect;
      if (!box) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setRect({ width: Math.round(box.width || 0), height: Math.round(box.height || 0) });
      });
    });

    observer.observe(node);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [ref]);

  return rect;
}

function HighExposureTrendChart({ points = [], symbol = "$", entityLabel = "" }) {
  const containerRef = useRef(null);
  const { width } = useResizeObserver(containerRef);
  const chartPoints = useMemo(
    () => safeArray(points).filter((point) => point && Number.isFinite(Number(point?.value))),
    [points],
  );

  const hasData = chartPoints.length >= 2;
  const svgWidth = Math.max(360, width || 0);
  const height = 220;
  const padL = 56;
  const padR = 18;
  const padT = 16;
  const padB = 36;

  const values = chartPoints.map((point) => Number(point.value));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const padding = Math.max(1000, Math.round((max - min || max || 1) * 0.15));
  const minY = Math.max(0, min - padding);
  const maxY = Math.max(minY + 1, max + padding);

  const xOf = (index) => {
    if (chartPoints.length <= 1) return padL + (svgWidth - padL - padR) / 2;
    return padL + (index * (svgWidth - padL - padR)) / (chartPoints.length - 1);
  };
  const yOf = (value) => {
    const ratio = (value - minY) / (maxY - minY || 1);
    return padT + (1 - clamp(ratio, 0, 1)) * (height - padT - padB);
  };

  const pathD = chartPoints
    .map((point, index) => `${index === 0 ? "M" : "L"} ${xOf(index).toFixed(2)} ${yOf(point.value).toFixed(2)}`)
    .join(" ");

  const ticks = Array.from({ length: 5 }, (_, index) => Math.round(minY + ((maxY - minY) / 4) * index));

  return (
    <div ref={containerRef} className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-black text-slate-900">High Exposure Trend</div>
          <div className="mt-1.5 text-[11px] text-slate-500">
            Last {chartPoints.length || 0} audits{entityLabel ? ` for ${entityLabel}` : ""}.
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs text-slate-600">
          Complete at least two audits to see the high exposure trend.
        </div>
      ) : (
        <div className="mt-6">
          <svg viewBox={`0 0 ${svgWidth} ${height}`} className="h-[210px] w-full">
            {ticks.map((tick) => {
              const y = yOf(tick);
              return (
                <g key={tick}>
                  <line x1={padL} y1={y} x2={svgWidth - padR} y2={y} stroke="#E2E8F0" strokeWidth="1" />
                  <text x={0} y={y + 4} fontSize="10" fill="#64748B">
                    {formatMoneyCompact(tick, symbol)}
                  </text>
                </g>
              );
            })}

            <path d={pathD} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

            {chartPoints.map((point, index) => (
              <g key={point.id}>
                <circle cx={xOf(index)} cy={yOf(point.value)} r="4.5" fill="#fff" stroke="#2563EB" strokeWidth="2" />
                <text x={xOf(index)} y={height - 8} textAnchor="middle" fontSize="10" fill="#64748B">
                  {point.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

function SnapshotPicker({ rows, selectedId, onSelect }) {
  const selected = safeArray(rows).find((row) => String(row?.snapshot_id) === String(selectedId || "")) || safeArray(rows)[0] || null;
  return (
    <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="text-sm font-black text-slate-900">Saved Audits</div>
      <div className="mt-1.5 text-[11px] text-slate-500">Select a past audit to view its results.</div>
      {safeArray(rows).length ? (
        <select
          value={selectedId || ""}
          onChange={(event) => onSelect(event.target.value || null)}
          className="mt-4 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-black text-slate-900 outline-none focus:border-blue-400"
        >
          {safeArray(rows).slice(0, 20).map((row) => {
            const date = formatDateTime(row?.created_at_iso);
            const scope = Array.isArray(row?.scope) && row.scope.length ? row.scope.join(", ") : "—";
            return <option key={row?.snapshot_id || row?.created_at_iso} value={row?.snapshot_id || ""}>{date} ({scope})</option>;
          })}
        </select>
      ) : (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No saved audits are available for the current business.</div>
      )}
      {selected ? <div className="mt-3 text-xs text-slate-500">Showing the selected audit's findings throughout this report.</div> : null}
    </div>
  );
}

function MetricCard({ label, value, symbol, className = "", accentClassName = "text-slate-900", subtext = "" }) {
  return (
    <div className={cn("rounded-[30px] border p-[26px] shadow-sm", className)}>
      <div className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-500">{label}</div>
      <div className={cn("mt-4 text-3xl font-black tracking-tight", accentClassName)}>{formatMoney(value, symbol)}</div>
      {subtext ? <div className="mt-3 text-xs leading-5 text-slate-500">{subtext}</div> : null}
    </div>
  );
}

function DeltaBadge({ label, delta, symbol }) {
  const tone = delta == null ? "text-slate-500" : delta > 0 ? "text-rose-700" : delta < 0 ? "text-emerald-700" : "text-slate-500";
  const prefix = delta == null ? "" : delta > 0 ? "+" : delta < 0 ? "−" : "";
  return (
    <div className="rounded-2xl border border-slate-300 bg-white px-4 py-4 shadow-sm">
      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className={cn("mt-2.5 text-[15px] font-black", tone)}>
        {delta == null ? "No prior audit comparison" : `${prefix}${formatMoney(Math.abs(delta), symbol)}`}
      </div>
    </div>
  );
}

function JurisdictionRow({ row }) {
  const symbol = guessCurrencySymbol(row?.currency || "USD");
  const isStateRow = row?.kind === "state";
  const hasStatePlanningMetadata = Boolean(
    isStateRow && (row?.planningBasisLabel || row?.legalBasis || row?.statutoryPenaltyBasis || row?.confidenceLevel),
  );

  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-black text-slate-900">{row?.label || "Jurisdiction"}</div>
          <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-slate-500">Native currency: {row?.currency || "—"}</div>
          {isStateRow ? (
            <div className="mt-2 text-xs font-black text-slate-700">
              {row?.planningBasisLabel || "State exposure planning estimate"}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {row?.scopeOnly ? <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-black text-slate-600">Included in scope</span> : null}
          {isStateRow && row?.confidenceLevel ? (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-black text-slate-600">
              Confidence: {row.confidenceLevel}
            </span>
          ) : null}
        </div>
      </div>

      {hasStatePlanningMetadata ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600">
          {row?.statutoryPenaltyBasis ? (
            <div>
              <span className="font-black text-slate-900">Penalty basis:</span> {row.statutoryPenaltyBasis}
            </div>
          ) : null}
          {row?.legalBasis ? (
            <div className="mt-1.5">
              <span className="font-black text-slate-900">Source basis:</span> {row.legalBasis}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-3.5 md:grid-cols-3">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">High</div>
          <div className="mt-2.5 text-xl font-black text-red-700">
            {row?.metrics?.present ? formatMoney(row?.metrics?.high, symbol) : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Expected</div>
          <div className="mt-2.5 text-xl font-black text-blue-700">
            {row?.metrics?.present ? formatMoney(row?.metrics?.expected, symbol) : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Low</div>
          <div className="mt-2.5 text-xl font-black text-emerald-700">
            {row?.metrics?.present ? formatMoney(row?.metrics?.low, symbol) : "—"}
          </div>
        </div>
      </div>

      {row?.fallbackState ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-900">
          <span className="font-black">Planning disclosure:</span>{" "}
          {row?.fallbackDisclosure || US_STATE_FALLBACK_DISCLOSURE}
        </div>
      ) : null}
    </div>
  );
}


function GovernanceAdjustedExposurePanel({ result, metrics, symbol = "$" }) {
  const layer2 = isRecord(result?.layer_2) ? result.layer_2 : null;
  if (!result?.enabled || !layer2 || !metrics?.available) {
    return (
      <section className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-sm">
        <div className="text-base font-black uppercase tracking-[0.24em] text-slate-700 sm:text-lg">Your Current Risk Position</div>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-600">
          Your Current Risk Position is unavailable for this snapshot because baseline exposure data is incomplete.
        </div>
      </section>
    );
  }

  const confidenceLabel = layer2?.confidence?.label || "—";
  const exposureDrivers = safeArray(layer2?.drivers?.top_exposure_drivers);
  const reductionDrivers = safeArray(layer2?.drivers?.top_risk_reduction_drivers);
  const profiles = isRecord(layer2?.profiles) ? layer2.profiles : {};
  const positionRatio = clamp(layer2?.exposure_position?.ratio ?? 0.5, 0, 1);
  const positionPct = Math.round(positionRatio * 100);

  return (
    <section className="rounded-[36px] border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-slate-50 p-7 shadow-sm ring-1 ring-blue-100">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-4xl">
          <div className="text-base font-black uppercase tracking-[0.24em] text-blue-800 sm:text-lg">Your Current Risk Position</div>
          <div className="mt-2 text-sm leading-6 text-slate-600">
            Your AI Risk Overview (above) summarizes the baseline exposure identified by your current audit. Your Current Risk Position provides a separate view of how that risk looks today based on your assessment answers, current practices, supporting evidence, remediation progress, user impact, data sensitivity, and regulatory environment.
          </div>
          <div className="mt-3 text-sm font-semibold leading-6 text-slate-700">
            Think of this as a current view of how your organization is managing its identified AI risk today, based on the information provided in your assessment and your current evidence and remediation status.
          </div>
          <div className="mt-3 rounded-2xl border border-blue-200 bg-white/80 px-4 py-3 text-xs font-semibold leading-5 text-blue-900">
            Because the Risk Overview may combine multiple jurisdictions and currencies for display, these figures should not be compared dollar-for-dollar with the Overview totals above.
          </div>
        </div>
        <div className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-right shadow-sm">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Confidence</div>
          <div className="mt-2 text-xl font-black text-blue-700">{confidenceLabel}</div>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <MetricCard
          label="High"
          value={metrics?.high}
          symbol={symbol}
          className="border-blue-200 bg-white"
          accentClassName="text-blue-800"
          subtext="Upper end of the governance-adjusted range"
        />
        <MetricCard
          label="Expected"
          value={metrics?.expected}
          symbol={symbol}
          className="border-blue-300 bg-blue-50"
          accentClassName="text-blue-700"
          subtext="Current governance-adjusted planning estimate"
        />
        <MetricCard
          label="Low"
          value={metrics?.low}
          symbol={symbol}
          className="border-emerald-200 bg-white"
          accentClassName="text-emerald-700"
          subtext="Lower end of the governance-adjusted range"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black text-slate-900">Exposure Position</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">Shows where your current adjusted estimate sits within the original range. Farther left is lower; farther right is higher.</div>
              <div className="mt-1 text-xs font-black text-blue-700">Your position: {layer2?.exposure_position?.label || "—"}</div>
            </div>
            <div className="text-xs font-black text-slate-500">LOW → HIGH</div>
          </div>
          <div className="mt-5 h-3 rounded-full bg-slate-200">
            <div className="relative h-3 rounded-full bg-gradient-to-r from-emerald-300 via-blue-300 to-red-300" style={{ width: "100%" }}>
              <div
                className="absolute top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border-4 border-white bg-slate-900 shadow"
                style={{ left: `calc(${positionPct}% - 12px)` }}
              />
            </div>
          </div>
          <div className={cn("mt-5 rounded-2xl border p-4 text-xs leading-5", layer2?.validation_gate?.passed ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900")}>
            <div className="font-black">Evidence Check</div>
            <div className="mt-1">{layer2?.validation_gate?.passed ? "Evidence supports this adjustment." : "More evidence is needed, so AFP limits the reduction until stronger support is available."}</div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-black text-slate-900">Risk Reduction Earned So Far</div>
          <div className="mt-3 text-3xl font-black text-slate-900">
            {layer2?.reduction_from_expected_pct == null ? "—" : `${Math.abs(Number(layer2.reduction_from_expected_pct || 0))}%`}
          </div>
          <div className="mt-1 text-sm font-black text-emerald-700">
            {safeNumber(metrics?.reduction) == null ? "—" : `${formatMoney(Math.abs(Number(metrics.reduction)), metrics?.symbol || symbol)} reduction currently recognized`}
          </div>
          <div className="mt-3 text-xs leading-5 text-slate-500">
            This is the reduction currently credited based on the risk-management progress reflected in your assessment, evidence, and remediation status so far. It is not the total risk-reduction opportunity available to you.
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-black text-slate-900">Factors Shaping This Estimate</div>
          <div className="mt-4 grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
            {[
              ["Jurisdiction Enforcement", profiles?.jurisdiction_enforcement?.label],
              ["Governance Maturity", profiles?.governance_maturity?.label],
              ["Evidence Readiness", profiles?.evidence_readiness?.label],
              ["Remediation Progress", profiles?.remediation_progress?.label],
              ["User Impact", profiles?.user_impact?.label],
              ["Data Sensitivity", profiles?.data_sensitivity?.label],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="font-black uppercase tracking-[0.18em] text-slate-500">{label}</div>
                <div className="mt-1.5 text-sm font-black text-slate-900">{value || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-black text-slate-900">Top Exposure Drivers</div>
            {exposureDrivers.length ? (
              <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
                {exposureDrivers.slice(0, 5).map((driver) => (
                  <li key={driver.id || driver.label} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-red-500" />
                    <span>{driver.label || driver}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No elevated exposure drivers identified.</div>
            )}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-black text-slate-900">Top Risk Reduction Drivers</div>
            {reductionDrivers.length ? (
              <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
                {reductionDrivers.slice(0, 5).map((driver) => (
                  <li key={driver.id || driver.label} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span>{driver.label || driver}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No material risk reduction drivers identified yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-blue-100 bg-white/80 p-4 text-xs leading-5 text-slate-600">
        {result?.public_model_notice || "Governance-adjusted exposure is a planning estimate and not legal advice, a legal conclusion, or a regulatory guarantee."}
        {metrics?.converted ? <span> Amounts in this panel are converted for display only.</span> : null}
        {metrics?.conversionMissing ? <span> Requested display-currency conversion was unavailable for this panel, so native Layer 2 currency is shown.</span> : null}
      </div>
    </section>
  );
}


function RemediationImpactPanel({ result, metrics, symbol = "$", onOpenRemediation, remediationUnavailable = false }) {
  const layer3 = isRecord(result?.layer_3) ? result.layer_3 : null;
  const governanceCredit = isRecord(result?.layer_2?.governance_credit) ? result.layer_2.governance_credit : {};
  const actions = safeArray(result?.layer_2?.drivers?.next_best_actions).slice(0, 3);

  if (!result?.enabled || !layer3 || !metrics?.available) {
    return (
      <section className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-sm">
        <div className="text-base font-black uppercase tracking-[0.24em] text-slate-700 sm:text-lg">Remediation Impact</div>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-600">
          Remediation Impact is unavailable for this snapshot because remediation data is incomplete.
        </div>
      </section>
    );
  }

  const findingsResolved = safeNumber(layer3?.findings_resolved);
  const findingsTotal = safeNumber(layer3?.findings_total);
  const evidenceCoveragePct = safeNumber(layer3?.evidence_coverage_pct);
  const criticalRemaining = safeNumber(layer3?.critical_findings_remaining);
  const governanceCreditEarnedPct = safeNumber(layer3?.governance_credit_earned_pct ?? governanceCredit?.final_earned_pct);
  const maxCreditPct = safeNumber(layer3?.maximum_governance_credit_available_pct ?? governanceCredit?.maximum_available_pct);
  const remainingOpportunityPct = safeNumber(layer3?.remaining_governance_opportunity_pct ?? governanceCredit?.remaining_opportunity_pct);
  const blockedOpportunityPct = safeNumber(layer3?.blocked_governance_opportunity_pct ?? governanceCredit?.blocked_opportunity_pct);

  return (
    <section className="rounded-[36px] border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-7 shadow-sm ring-1 ring-emerald-100">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-4xl">
          <div className="text-base font-black uppercase tracking-[0.24em] text-emerald-800 sm:text-lg">
            {layer3.title || "Remediation Impact"}
          </div>
          <div className="mt-2 text-sm leading-6 text-slate-600">
            Shows how much modeled exposure associated with identified findings has been addressed through remediation, and how much remains unresolved.
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-right shadow-sm">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Governance Credit</div>
          <div className="mt-2 text-xl font-black text-emerald-700">
            {governanceCreditEarnedPct == null ? "—" : `${governanceCreditEarnedPct}%`}
          </div>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <MetricCard
          label="Reduced Through Remediation"
          value={metrics?.exposureReduction}
          symbol={symbol}
          className="border-emerald-200 bg-white"
          accentClassName="text-emerald-700"
          subtext="Modeled exposure associated with remediated findings"
        />
        <MetricCard
          label="Remaining Unresolved"
          value={metrics?.remainingUnresolved}
          symbol={symbol}
          className="border-amber-200 bg-amber-50"
          accentClassName="text-amber-700"
          subtext="Modeled finding exposure still requiring action"
        />
        <MetricCard
          label="Total Modeled Findings"
          value={metrics?.totalModeled}
          symbol={symbol}
          className="border-slate-200 bg-white"
          accentClassName="text-slate-900"
          subtext="Total modeled exposure associated with identified findings"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Findings Resolved</div>
          <div className="mt-2 text-2xl font-black text-slate-900">
            {findingsResolved == null || findingsTotal == null ? "—" : `${findingsResolved}/${findingsTotal}`}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Evidence Coverage</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{evidenceCoveragePct == null ? "—" : `${evidenceCoveragePct}%`}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Critical Open</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{criticalRemaining == null ? "—" : criticalRemaining}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Opportunity</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{remainingOpportunityPct == null ? "—" : `${remainingOpportunityPct}%`}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-black text-slate-900">Governance Benefit Position</div>
          <div className="mt-4 space-y-3 text-xs leading-5 text-slate-600">
            <div className="flex justify-between gap-4"><span>Current Governance Credit Earned</span><span className="font-black text-slate-900">{governanceCreditEarnedPct == null ? "—" : `${governanceCreditEarnedPct}%`}</span></div>
            <div className="flex justify-between gap-4"><span>Maximum Governance Credit Available</span><span className="font-black text-slate-900">{maxCreditPct == null ? "—" : `${maxCreditPct}%`}</span></div>
            <div className="flex justify-between gap-4"><span>Remaining Governance Opportunity</span><span className="font-black text-slate-900">{remainingOpportunityPct == null ? "—" : `${remainingOpportunityPct}%`}</span></div>
            <div className="flex justify-between gap-4"><span>Blocked by Constraints</span><span className="font-black text-slate-900">{blockedOpportunityPct == null ? "—" : `${blockedOpportunityPct}%`}</span></div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-black text-slate-900">Where to Focus First</div>
          {actions.length ? (
            <div className="mt-4 space-y-3">
              {actions.map((action) => (
                <div key={action?.id || action?.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-700">
                  <div className="font-black text-slate-900">{action?.label || "Recommended action"}</div>
                  <div className="mt-1">{action?.rationale || "This action may improve governance defensibility."}</div>
                  {safeNumber(action?.estimated_governance_benefit_pct) != null ? (
                    <div className="mt-2 font-black text-emerald-700">Estimated governance benefit: {action.estimated_governance_benefit_pct}%</div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600">
              No additional next-best action is available for this snapshot.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-[28px] border border-emerald-200 bg-white p-5 shadow-sm">
        <div className="text-base font-black text-slate-900">Ready to start improving your risk position?</div>
        <div className="mt-2 text-sm leading-6 text-slate-600">
          Go to Remediation to work through identified findings, document corrective actions, attach supporting evidence, and track your progress.
        </div>
        <button
          type="button"
          onClick={onOpenRemediation}
          disabled={remediationUnavailable || typeof onOpenRemediation !== "function"}
          title={remediationUnavailable ? "Remediation is not included in this plan." : "Go to Remediation"}
          className={cn(
            "mt-4 rounded-2xl px-5 py-3 text-sm font-black",
            remediationUnavailable || typeof onOpenRemediation !== "function"
              ? "cursor-not-allowed bg-slate-100 text-slate-400"
              : "bg-blue-600 text-white hover:bg-blue-700",
          )}
        >
          {remediationUnavailable ? "Remediation Unavailable" : "Go to Remediation →"}
        </button>
      </div>

      <div className="mt-5 rounded-2xl border border-emerald-100 bg-white/80 p-4 text-xs leading-5 text-slate-600">
        {layer3.public_notice || "Layer 3 measures modeled exposure associated with findings addressed through remediation. It does not predict future fines prevented or eliminate historical exposure."}
        {metrics?.converted ? <span> Amounts in this panel are converted for display only.</span> : null}
        {metrics?.conversionMissing ? <span> Requested display-currency conversion was unavailable for this panel, so native Layer 3 currency is shown.</span> : null}
      </div>
    </section>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const { loading: entitlementLoading, allowRemediation } = useEntitlement();
  const remediationUnavailable = entitlementLoading || !allowRemediation;
  const [activeBizId, setActiveBizId] = useState(() => safeGetActiveBusinessId());
  const [index, setIndex] = useState(() => safeReadIndex({ businessId: safeGetActiveBusinessId() }));
  const [selectedId, setSelectedId] = useState(() => {
    const rows = safeReadIndex({ businessId: safeGetActiveBusinessId() });
    return rows?.[0]?.snapshot_id ?? null;
  });
  const [displayCurrency, setDisplayCurrency] = useState(() => readDisplayCurrencyPref());
  const [fxLive, setFxLive] = useState({ status: "idle", cache: null, error: null, last_refreshed_iso: null });
  const [useLiveFxForDisplay, setUseLiveFxForDisplay] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [showAllRegulatorySources, setShowAllRegulatorySources] = useState(false);
  const [statusNote, setStatusNote] = useState("");
  const [paidQuotaUsage, setPaidQuotaUsage] = useState(null);
  const statusTimeoutRef = useRef(null);

  const clearStatusTimeout = useCallback(() => {
    if (statusTimeoutRef.current) {
      window.clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  }, []);

  const setTransientStatusNote = useCallback(
    (message, ms = 1800) => {
      clearStatusTimeout();
      setStatusNote(message || "");
      if (!message) return;
      statusTimeoutRef.current = window.setTimeout(() => {
        setStatusNote("");
        statusTimeoutRef.current = null;
      }, ms);
    },
    [clearStatusTimeout],
  );

  const refresh = useCallback(({ keepSelection = true } = {}) => {
    const businessId = safeGetActiveBusinessId();
    const rows = safeReadIndex({ businessId });
    setActiveBizId(businessId);
    setIndex(rows);
    setSelectedId((current) => {
      const currentKey = current == null ? null : String(current);
      if (keepSelection && currentKey && rows.some((row) => String(row?.snapshot_id) === currentKey)) {
        return current;
      }
      return rows?.[0]?.snapshot_id ?? null;
    });
  }, []);

  useEffect(() => {
    refresh({ keepSelection: true });

    const handleFocus = () => refresh({ keepSelection: true });
    const handleStorage = (event) => {
      if (!event?.key) return;
      const key = String(event.key);
      if (key === ACTIVE_BIZ_KEY || key.startsWith("aifine_audit_snapshot_index")) {
        refresh({ keepSelection: true });
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleStorage);
    };
  }, [refresh]);

  // Recover authoritative Pro/Enterprise snapshots across refreshes and devices.
  // Existing legacy snapshots are never deleted, renamed, or filtered.
  useEffect(() => {
    let cancelled = false;
    hydrateServerPaidAssessments().then(({ count, usage, incomplete }) => {
      if (cancelled) return;
      setPaidQuotaUsage(usage);
      if (count > 0) refresh({ keepSelection: true });
      if (incomplete) setTransientStatusNote("Some server-saved reports could not be cached on this device. Existing reports remain available.", 10000);
    }).catch(() => {
      // An unavailable history endpoint never hides existing local reports.
    });
    return () => { cancelled = true; };
  }, [refresh, setTransientStatusNote]);

  useEffect(() => () => clearStatusTimeout(), [clearStatusTimeout]);
  useEffect(() => {
    if (displayCurrency) writeDisplayCurrencyPref(displayCurrency);
  }, [displayCurrency]);

  const selectedPayload = useMemo(() => (selectedId ? safeGetSnapshotPayload(selectedId) : null), [selectedId]);
  const selectedMeta = useMemo(
    () => safeArray(index).find((row) => String(row?.snapshot_id) === String(selectedId || "")) || null,
    [index, selectedId],
  );
  const selectedExposure = useMemo(() => normalizeExposure(selectedPayload?.risk_exposure || null), [selectedPayload]);
  const snapshotFx = useMemo(() => extractAuditFxCache(selectedPayload), [selectedPayload]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setFxLive((prev) => ({ ...prev, status: "loading", error: null }));
        setUseLiveFxForDisplay(false);
        const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: false });
        if (!alive) return;
        setFxLive({ status: "ready", cache, error: null, last_refreshed_iso: null });
      } catch (error) {
        if (!alive) return;
        setFxLive({ status: "error", cache: null, error: String(error?.message || error || "FX error"), last_refreshed_iso: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const liveFx = useMemo(() => normalizeFxCache(fxLive?.cache || null), [fxLive?.cache]);
  const fxForDisplay = useMemo(() => {
    if (useLiveFxForDisplay && liveFx) return liveFx;
    return snapshotFx || liveFx || null;
  }, [snapshotFx, liveFx, useLiveFxForDisplay]);

  const calculationCurrency = getCalculationCurrency(selectedExposure);
  const availableCurrencies = getAvailableCurrencies(selectedExposure);
  const displayCurrencyOptions = useMemo(() => {
    const baseline = new Set(availableCurrencies);
    if (fxForDisplay) PRIORITY_DISPLAY_CURRENCIES.forEach((code) => baseline.add(code));
    const ordered = PRIORITY_DISPLAY_CURRENCIES.filter((code) => baseline.has(code));
    const remainder = Array.from(baseline).filter((code) => !ordered.includes(code)).sort();
    return [...ordered, ...remainder];
  }, [availableCurrencies, fxForDisplay]);

  const effectiveDisplayCurrency = useMemo(() => {
    if (displayCurrency && displayCurrencyOptions.includes(displayCurrency)) return displayCurrency;
    return calculationCurrency || displayCurrencyOptions[0] || availableCurrencies[0] || "";
  }, [displayCurrency, displayCurrencyOptions, calculationCurrency, availableCurrencies]);


  useEffect(() => {
    setUseLiveFxForDisplay(false);
  }, [selectedId]);

  const fxDisplayMeta = useMemo(() => {
    const snapshotAvailable = Boolean(snapshotFx);
    const liveAvailable = Boolean(liveFx);
    const usingLive = Boolean(useLiveFxForDisplay && liveFx);
    const active = fxForDisplay || null;

    return {
      snapshotAvailable,
      liveAvailable,
      usingLive,
      activeLabel: usingLive
        ? "Latest refreshed rates for display only"
        : snapshotAvailable
          ? "Stored snapshot rates"
          : liveAvailable
            ? "Latest available rates"
            : "No FX cache available",
      activeSource: active?.source_label || "—",
      activeAsOf: formatDateTime(active?.as_of_iso),
      activeRetrieved: formatDateTime(active?.retrieved_at_iso),
      snapshotAsOf: formatDateTime(snapshotFx?.as_of_iso),
      snapshotRetrieved: formatDateTime(snapshotFx?.retrieved_at_iso),
      liveAsOf: formatDateTime(liveFx?.as_of_iso),
      liveRetrieved: formatDateTime(liveFx?.retrieved_at_iso),
      lastManualRefresh: formatDateTime(fxLive?.last_refreshed_iso),
    };
  }, [snapshotFx, liveFx, fxForDisplay, useLiveFxForDisplay, fxLive?.last_refreshed_iso]);

  const heroMetrics = useMemo(
    () =>
      resolveDisplayMetrics({
        exposure: selectedExposure,
        displayCurrencyCode: effectiveDisplayCurrency,
        fxCache: fxForDisplay,
      }),
    [selectedExposure, effectiveDisplayCurrency, fxForDisplay],
  );

  const evidenceSummaryForLayer2 = useMemo(() => extractEvidenceSummaryForLayer2(selectedPayload), [selectedPayload]);
  const remediationSummaryForLayer2 = useMemo(() => extractRemediationSummaryForLayer2(selectedPayload), [selectedPayload]);
  const governanceAdjustedExposure = useMemo(
    () =>
      computeGovernanceAdjustedExposure({
        exposure: selectedExposure,
        canonicalPayload: selectedPayload,
        evidenceSummary: evidenceSummaryForLayer2,
        remediationSummary: remediationSummaryForLayer2,
      }),
    [selectedExposure, selectedPayload, evidenceSummaryForLayer2, remediationSummaryForLayer2],
  );
  const governanceDisplayMetrics = useMemo(
    () =>
      resolveLayer2DisplayMetrics({
        governanceAdjusted: governanceAdjustedExposure,
        displayCurrencyCode: heroMetrics?.displayCurrencyCode || effectiveDisplayCurrency,
        fxCache: fxForDisplay,
      }),
    [governanceAdjustedExposure, heroMetrics?.displayCurrencyCode, effectiveDisplayCurrency, fxForDisplay],
  );

  const remediationImpactMetrics = useMemo(
    () =>
      resolveLayer3DisplayMetrics({
        governanceAdjusted: governanceAdjustedExposure,
        displayCurrencyCode: heroMetrics?.displayCurrencyCode || effectiveDisplayCurrency,
        fxCache: fxForDisplay,
      }),
    [governanceAdjustedExposure, heroMetrics?.displayCurrencyCode, effectiveDisplayCurrency, fxForDisplay],
  );

  const entityName = selectedPayload?.entity_name || selectedMeta?.entity_name || "Not specified";
  const createdAtIso =
    selectedPayload?.created_at_iso ||
    selectedPayload?.generated_at ||
    selectedPayload?.meta?.created_at_iso ||
    selectedMeta?.created_at_iso ||
    null;
  const scope = safeArray(selectedPayload?.summary?.jurisdictions || selectedMeta?.scope || selectedPayload?.jurisdictions || []);
  const scopeText = scope.length ? scope.join(", ") : "—";

  const counts = useMemo(() => pickCounts(selectedPayload), [selectedPayload]);
  const riskScore = useMemo(() => resolvePrimaryRiskScore({ payload: selectedPayload, meta: selectedMeta }), [selectedPayload, selectedMeta]);
  const protectionScore = useMemo(() => {
    const score = safeNumber(riskScore);
    return score == null ? null : Math.round(clamp(100 - score, 0, 100));
  }, [riskScore]);
  const riskGrade = getRiskGrade(riskScore, selectedPayload?.assessment?.risk_grade || selectedMeta?.risk_grade);

  const previousRow = useMemo(() => {
    const currentIndex = safeArray(index).findIndex((row) => String(row?.snapshot_id) === String(selectedId || ""));
    return currentIndex >= 0 ? index[currentIndex + 1] || null : null;
  }, [index, selectedId]);

  const previousMetrics = useMemo(() => {
    if (!previousRow?.snapshot_id) return null;
    const payload = safeGetSnapshotPayload(previousRow.snapshot_id);
    const exposure = normalizeExposure(payload?.risk_exposure || null);
    const fxCache = extractAuditFxCache(payload) || liveFx || null;
    return resolveDisplayMetrics({ exposure, displayCurrencyCode: effectiveDisplayCurrency, fxCache });
  }, [previousRow, effectiveDisplayCurrency, liveFx]);

  const highDelta = useMemo(() => {
    const current = safeNumber(heroMetrics?.high);
    const prior = safeNumber(previousMetrics?.high);
    return current != null && prior != null ? Math.round(current - prior) : null;
  }, [heroMetrics, previousMetrics]);
  const expectedDelta = useMemo(() => {
    const current = safeNumber(heroMetrics?.expected);
    const prior = safeNumber(previousMetrics?.expected);
    return current != null && prior != null ? Math.round(current - prior) : null;
  }, [heroMetrics, previousMetrics]);
  const lowDelta = useMemo(() => {
    const current = safeNumber(heroMetrics?.low);
    const prior = safeNumber(previousMetrics?.low);
    return current != null && prior != null ? Math.round(current - prior) : null;
  }, [heroMetrics, previousMetrics]);

  const perRegime = useMemo(
    () => (isRecord(selectedExposure?.per_regime) ? selectedExposure.per_regime : {}),
    [selectedExposure],
  );
  const regimeSources = useMemo(
    () =>
      deriveRegulatorySources({
        perRegime,
        displayCurrencyCode: heroMetrics?.displayCurrencyCode || effectiveDisplayCurrency,
        fxCache: fxForDisplay,
        totalHighExposure: heroMetrics?.high,
      }),
    [perRegime, heroMetrics, effectiveDisplayCurrency, fxForDisplay],
  );
  const visibleRegimeSources = useMemo(
    () => (showAllRegulatorySources ? regimeSources : regimeSources.slice(0, MAX_VISIBLE_REGULATORY_PILLS)),
    [regimeSources, showAllRegulatorySources],
  );
  const hiddenRegimeCount = Math.max(0, regimeSources.length - MAX_VISIBLE_REGULATORY_PILLS);
  const drivers = useMemo(
    () => deriveExposureDrivers({ payload: selectedPayload, counts, regimeSources, scorePct: riskScore }),
    [selectedPayload, counts, regimeSources, riskScore],
  );

  const jurisdictionBreakdown = useMemo(
    () => deriveJurisdictionBreakdown({ payload: selectedPayload, meta: selectedMeta }),
    [selectedPayload, selectedMeta],
  );
  const jurisdictionRows = jurisdictionBreakdown.rows || [];
  const generalJurisdictionRows = jurisdictionRows.filter((row) => row.kind !== "state");
  const stateJurisdictionRows = jurisdictionRows.filter((row) => row.kind === "state");

  const totalCeilingDisplay = useMemo(() => {
    let total = 0;
    let found = false;

    Object.values(perRegime || {}).forEach((item) => {
      const entry = isRecord(item) ? item : {};
      const ceiling = safeNumber(entry?.statutory_ceiling_native);
      const nativeCode = String(entry?.currency_code || heroMetrics?.displayCurrencyCode || "").toUpperCase();
      if (ceiling == null) return;
      if (!nativeCode || nativeCode === heroMetrics?.displayCurrencyCode) {
        total += ceiling;
        found = true;
        return;
      }
      if (!fxForDisplay) return;
      const converted = convertMoney(ceiling, nativeCode, heroMetrics?.displayCurrencyCode, fxForDisplay);
      if (typeof converted === "number") {
        total += converted;
        found = true;
      }
    });

    return found ? Math.round(total) : null;
  }, [perRegime, heroMetrics?.displayCurrencyCode, fxForDisplay]);

  const rowMetricsMap = useMemo(() => {
    const ids = new Set();
    safeArray(index).slice(0, 25).forEach((row) => {
      if (row?.snapshot_id) ids.add(String(row.snapshot_id));
    });
    if (selectedId) ids.add(String(selectedId));

    const out = {};
    Array.from(ids).forEach((snapshotId) => {
      const payload = safeGetSnapshotPayload(snapshotId);
      const exposure = normalizeExposure(payload?.risk_exposure || null);
      const fxCache = extractAuditFxCache(payload) || liveFx || null;
      out[snapshotId] = resolveDisplayMetrics({
        exposure,
        displayCurrencyCode: effectiveDisplayCurrency,
        fxCache,
      });
    });
    return out;
  }, [index, selectedId, liveFx, effectiveDisplayCurrency]);

  const trendPoints = useMemo(
    () => getTrendPoints({ rows: safeArray(index).slice(0, 6), rowMetricsMap }),
    [index, rowMetricsMap],
  );

  const explainText = {
    protectionScore:
      "Higher is better. Protection Score = 100 minus AFP’s certified underlying Risk Score. This is a presentation change only; it does not change the saved assessment, Risk Grade, findings, or exposure calculations.",
    riskGrade:
      "Risk Grade continues to use the certified underlying assessment score. A indicates lower estimated regulatory risk; E indicates higher estimated regulatory risk.",
    high: "High is the model’s conservative headline estimate for the selected audit snapshot. It is a planning estimate, not legal advice.",
    expected: "Expected is the model’s mid-range planning estimate after practical reducing factors are considered.",
    low: "Low is the lower planning anchor used for budgeting and prioritization. Real outcomes can still be above or below it.",
    currency: "Only the combined total responds to the display currency selector. Jurisdiction rows stay in stored native currency.",
  };

  const handleRefreshLiveFx = async () => {
    try {
      setFxLive({ status: "loading", cache: null, error: null, last_refreshed_iso: null });
      const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: true });
      setFxLive({ status: "ready", cache, error: null, last_refreshed_iso: new Date().toISOString() });
      setTransientStatusNote("Display FX refreshed.");
    } catch (error) {
      setFxLive((prev) => ({ ...prev, status: "error", error: String(error?.message || error || "FX refresh error") }));
      setTransientStatusNote("FX refresh failed. Existing rates will continue where available.", 2400);
    }
  };

  const handleExportSnapshotPdf = () => {
    if (!selectedId) return;
    const payload = safeGetSnapshotPayload(selectedId);
    if (!payload) {
      setTransientStatusNote("Snapshot data is unavailable for PDF export.", 2200);
      return;
    }
    try {
      downloadAssessmentSnapshotPdf(payload, { snapshotId: selectedId });
      setTransientStatusNote("Snapshot PDF exported.");
    } catch {
      setTransientStatusNote("Snapshot PDF export failed.", 2200);
    }
  };

  const handleViewRemediation = () => {
    if (!selectedId) return;
    if (remediationUnavailable) {
      setTransientStatusNote(
        entitlementLoading
          ? "Checking remediation access…"
          : "Remediation is not included in this plan. Upgrade to a plan with remediation access to use the Remediation Hub.",
        3200,
      );
      return;
    }
    safeWriteRemediationContext(buildRemediationContext({ snapshotId: selectedId, payload: selectedPayload, meta: selectedMeta }));
    navigate("/remediation");
  };

  const handleViewSnapshot = () => {
    if (!selectedId) return;
    navigate(`/audit-snapshot/${encodeURIComponent(String(selectedId))}`);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      <Sidebar />

      <main className="flex-1 overflow-auto px-8 py-10 md:px-12 md:py-12 xl:px-14 xl:py-14">
        <div className="mx-auto w-full max-w-[1540px]">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <div className="text-3xl font-extrabold tracking-tight text-slate-900">Reports</div>
              <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-xl border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-sm">
                  Entity: <span className="font-black">{entityName}</span>
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                  Scope: <span className="font-black">{scopeText}</span>
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                  Mode: <span className="font-black">Executive</span>
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                  Created: <span className="font-black">{formatDateTime(createdAtIso)}</span>
                </span>
              </div>
              {paidQuotaUsage ? (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-700">
                  Assessments used: {paidQuotaUsage.used} of {paidQuotaUsage.allowed}.
                  {" "}Remaining: {Math.max(0, paidQuotaUsage.allowed - paidQuotaUsage.used)}.
                  {" "}Next allowance period: {new Date(paidQuotaUsage.next_reset).toLocaleDateString()}.
                </div>
              ) : null}
              {statusNote ? (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-700">
                  <span className="font-black">Status:</span> {statusNote}
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={handleExportSnapshotPdf}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black hover:border-slate-300"
              >
                Export Risk Report
              </button>
              <button
                type="button"
                onClick={handleViewRemediation}
                disabled={remediationUnavailable}
                title={remediationUnavailable ? "Remediation is not included in this plan." : "Open Remediation Hub"}
                className={cn(
                  "rounded-2xl border px-4 py-2.5 text-sm font-black",
                  remediationUnavailable
                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                    : "border-slate-200 bg-white hover:border-slate-300",
                )}
              >
                {remediationUnavailable ? "Remediation Unavailable" : "View Remediation"}
              </button>
              <button
                type="button"
                onClick={handleViewSnapshot}
                className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white hover:bg-slate-800"
              >
                View Snapshot
              </button>
            </div>
          </div>

          <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-sm font-black uppercase tracking-[0.35em] text-slate-500">Reports Available</div>
                <div className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                  Risk Reports export directly from this page. Remediation and Evidence &amp; Defense Reports open from the Remediation Hub so they stay tied to the selected audit's live remediation status and evidence records.
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-black text-slate-900">Risk Report</div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Executive snapshot of combined exposure, jurisdiction detail, and planning ranges for the selected audit snapshot.
                </div>
                <button
                  type="button"
                  onClick={handleExportSnapshotPdf}
                  className="mt-5 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-700"
                >
                  Export Risk Report
                </button>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-black text-slate-900">Remediation Report</div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Locked-snapshot remediation register showing actionable findings, status progression, and remaining exposure.
                </div>
                <button
                  type="button"
                  onClick={handleViewRemediation}
                  disabled={remediationUnavailable}
                  title={remediationUnavailable ? "Remediation is not included in this plan." : "Open Remediation Hub"}
                  className={cn(
                    "mt-5 rounded-2xl border px-4 py-2.5 text-sm font-black",
                    remediationUnavailable
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      : "border-slate-200 bg-white hover:border-slate-300",
                  )}
                >
                  {remediationUnavailable ? "Remediation Unavailable" : "Open in Remediation Hub"}
                </button>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-black text-slate-900">Evidence &amp; Defense Report</div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Evidence-focused view of uploaded files and links, the remediation questions they support, and the exposure still left unsupported.
                </div>
                <button
                  type="button"
                  onClick={handleViewRemediation}
                  disabled={remediationUnavailable}
                  title={remediationUnavailable ? "Remediation is not included in this plan." : "Open Remediation Hub"}
                  className={cn(
                    "mt-5 rounded-2xl border px-4 py-2.5 text-sm font-black",
                    remediationUnavailable
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      : "border-slate-200 bg-white hover:border-slate-300",
                  )}
                >
                  {remediationUnavailable ? "Remediation Unavailable" : "Open in Remediation Hub"}
                </button>
              </div>
            </div>
          </section>

          <div className="mt-10 grid grid-cols-1 gap-8 xl:grid-cols-[380px_minmax(0,1fr)]">
            <div className="space-y-8">
              <SnapshotPicker rows={index} selectedId={selectedId} onSelect={setSelectedId} />

              <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-black text-slate-900">Display Currency</div>
                    <div className="mt-1.5 text-[11px] leading-5 text-slate-500">
                      Show combined report amounts in your preferred currency. This does not change the saved audit.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={effectiveDisplayCurrency}
                      onChange={(event) => setDisplayCurrency(String(event.target.value || "").toUpperCase())}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-black text-slate-900 outline-none"
                    >
                      {displayCurrencyOptions.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleRefreshLiveFx}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-black hover:border-slate-300"
                    >
                      <RefreshCcw size={14} />
                      Refresh FX
                    </button>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-[18px] text-xs leading-5 text-slate-700">
                  <div>
                    <span className="font-black">Combined total display currency:</span> {heroMetrics?.displayCurrencyCode || "—"}
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">Current FX basis:</span> {fxDisplayMeta.activeLabel}
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">Conversion mode:</span>{" "}
                    {heroMetrics?.converted ? "Converted for display only" : "Shown in stored calculation/native currency"}
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">How Refresh FX works:</span> Refresh updates the combined total display with the latest available rates. It does not overwrite the stored snapshot or historical report data.
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">FX source in use:</span> {fxDisplayMeta.activeSource}
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">Rate date/time in use:</span> {fxDisplayMeta.activeAsOf}
                  </div>
                  <div className="mt-1.5">
                    <span className="font-black">Rates retrieved:</span> {fxDisplayMeta.activeRetrieved}
                  </div>
                  {fxDisplayMeta.snapshotAvailable ? (
                    <div className="mt-1.5">
                      <span className="font-black">Stored snapshot rate date/time:</span> {fxDisplayMeta.snapshotAsOf} <span className="text-slate-500">(retrieved {fxDisplayMeta.snapshotRetrieved})</span>
                    </div>
                  ) : null}
                  {fxDisplayMeta.liveAvailable ? (
                    <div className="mt-1.5">
                      <span className="font-black">Latest available rate date/time:</span> {fxDisplayMeta.liveAsOf} <span className="text-slate-500">(retrieved {fxDisplayMeta.liveRetrieved})</span>
                    </div>
                  ) : null}
                  {fxDisplayMeta.lastManualRefresh !== "—" ? (
                    <div className="mt-1.5">
                      <span className="font-black">Last manual refresh:</span> {fxDisplayMeta.lastManualRefresh}
                    </div>
                  ) : null}
                  {heroMetrics?.missing?.length ? (
                    <div className="mt-1.5">
                      <span className="font-black">Unavailable FX legs:</span> {heroMetrics.missing.join(", ")}
                    </div>
                  ) : null}
                </div>
              </div>

              <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[12px] font-black uppercase tracking-[0.22em] text-slate-500">Audit Snapshot Summary</div>
                <div className="mt-5 space-y-3.5 text-sm text-slate-700">
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                    <span className="font-black text-slate-900">Controls needing action</span>
                    <span>{counts.failed}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                    <span className="font-black text-slate-900">Controls needing review</span>
                    <span>{counts.unsure}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                    <span className="font-black text-slate-900">Total questions</span>
                    <span>{counts.total}</span>
                  </div>
                </div>
              </section>

              <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[12px] font-black uppercase tracking-[0.22em] text-slate-500">Exposure by Regulatory Source</div>
                <div className="mt-2.5 text-sm leading-6 text-slate-600">
                  Share of the combined high estimate associated with each regulatory source, shown in the selected display currency.
                </div>

                {regimeSources.length ? (
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    {visibleRegimeSources.map((item) => (
                      <span
                        key={item.key}
                        className={cn(
                          "max-w-full rounded-full border px-3 py-1.5 text-xs font-black leading-5 shadow-sm",
                          getRegimePillClasses(item),
                        )}
                        title={item.label}
                      >
                        {item.label}
                        {item.pct != null ? ` ${item.pct}%` : ""}
                      </span>
                    ))}
                    {hiddenRegimeCount > 0 && !showAllRegulatorySources ? (
                      <button
                        type="button"
                        onClick={() => setShowAllRegulatorySources(true)}
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-black leading-5 text-slate-700 hover:bg-slate-50"
                      >
                        +{hiddenRegimeCount} more
                      </button>
                    ) : null}
                    {hiddenRegimeCount > 0 && showAllRegulatorySources ? (
                      <button
                        type="button"
                        onClick={() => setShowAllRegulatorySources(false)}
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-black leading-5 text-slate-700 hover:bg-slate-50"
                      >
                        Show less
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                    No regulatory source data is stored for this snapshot.
                  </div>
                )}
              </section>
            </div>

            <div className="space-y-8">
              <section className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div>
                    <div className="text-base font-black uppercase tracking-[0.24em] text-slate-700 sm:text-lg">Your AI Risk Overview</div>
                    <div className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                      Your overall AI regulatory risk based on the current audit, before governance, supporting evidence and remediation progress are factored in.
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3.5 shadow-sm">
                      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">Protection Score</div>
                      <div className="mt-2.5 text-3xl font-black text-slate-900">{protectionScore == null ? "—" : `${protectionScore} / 100`}</div>
                      <div className="mt-1.5 text-[11px] font-semibold text-slate-500">Higher scores indicate stronger protection and lower estimated regulatory risk.</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Risk Grade</div>
                      <div className="mt-2.5 text-3xl font-black text-slate-900">{riskGrade}</div>
                      <div className="mt-1.5 text-[11px] font-semibold text-slate-500">A is lower risk. E is higher risk.</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Planning Ceiling</div>
                      <div className="mt-2.5 text-xl font-black text-slate-900">{formatMoney(totalCeilingDisplay, heroMetrics?.symbol)}</div>
                      <div className="mt-1.5 text-[11px] font-semibold leading-4 text-slate-500">Reference ceiling based on applicable statutory maximums. This is NOT your estimated exposure and is shown for context only.</div>
                    </div>
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 gap-5 xl:grid-cols-3">
                  <MetricCard
                    label="High"
                    value={heroMetrics?.high}
                    symbol={heroMetrics?.symbol}
                    className="border-red-200 bg-red-50"
                    accentClassName="text-red-700"
                    subtext="Conservative headline planning estimate"
                  />
                  <MetricCard
                    label="Expected"
                    value={heroMetrics?.expected}
                    symbol={heroMetrics?.symbol}
                    className="border-blue-200 bg-blue-50"
                    accentClassName="text-blue-700"
                    subtext="Mid-range planning estimate"
                  />
                  <MetricCard
                    label="Low"
                    value={heroMetrics?.low}
                    symbol={heroMetrics?.symbol}
                    className="border-emerald-200 bg-emerald-50"
                    accentClassName="text-emerald-700"
                    subtext="Lower planning anchor"
                  />
                </div>

                <div className="mt-6 grid grid-cols-1 gap-3.5 md:grid-cols-3">
                  <DeltaBadge label="High vs prior audit" delta={highDelta} symbol={heroMetrics?.symbol} />
                  <DeltaBadge label="Expected vs prior audit" delta={expectedDelta} symbol={heroMetrics?.symbol} />
                  <DeltaBadge label="Low vs prior audit" delta={lowDelta} symbol={heroMetrics?.symbol} />
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowExplain((value) => !value)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-black hover:bg-slate-50"
                  >
                    <Info size={14} />
                    How these results work
                    {showExplain ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>

                {showExplain ? (
                  <div className="mt-5 space-y-2.5 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs leading-5 text-slate-700">
                    <div>
                      <span className="font-black text-slate-900">Protection Score:</span> {explainText.protectionScore}
                    </div>
                    <div>
                      <span className="font-black text-slate-900">Risk Grade:</span> {explainText.riskGrade}
                    </div>
                    <div>
                      <span className="font-black text-slate-900">High:</span> {explainText.high}
                    </div>
                    <div>
                      <span className="font-black text-slate-900">Expected:</span> {explainText.expected}
                    </div>
                    <div>
                      <span className="font-black text-slate-900">Low:</span> {explainText.low}
                    </div>
                    <div>
                      <span className="font-black text-slate-900">Currency:</span> {explainText.currency}
                    </div>
                  </div>
                ) : null}
              </section>

              <GovernanceAdjustedExposurePanel
                result={governanceAdjustedExposure}
                metrics={governanceDisplayMetrics}
                symbol={governanceDisplayMetrics?.symbol || heroMetrics?.symbol || "$"}
              />

              <RemediationImpactPanel
                result={governanceAdjustedExposure}
                metrics={remediationImpactMetrics}
                symbol={remediationImpactMetrics?.symbol || governanceDisplayMetrics?.symbol || heroMetrics?.symbol || "$"}
                onOpenRemediation={handleViewRemediation}
                remediationUnavailable={remediationUnavailable}
              />

              <section className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-[12px] font-black uppercase tracking-[0.22em] text-slate-500">Exposure by Jurisdiction</div>
                  </div>
                </div>

                <div className="mt-6 space-y-[18px]">
                  {generalJurisdictionRows.length ? generalJurisdictionRows.map((row) => <JurisdictionRow key={row.key} row={row} />) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                      No stored jurisdiction-level breakout is available for UK, EU, or US Federal in this snapshot.
                    </div>
                  )}
                </div>

                {stateJurisdictionRows.length ? (
                  <div className="mt-8">
                    <div className="text-[12px] font-black uppercase tracking-[0.22em] text-slate-500">US States</div>
                    <div className="mt-5 space-y-[18px]">
                      {stateJurisdictionRows.map((row) => (
                        <JurisdictionRow key={row.key} row={row} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-[12px] font-black uppercase tracking-[0.22em] text-slate-500">Key Exposure Drivers</div>
                  <div className="mt-2.5 text-sm leading-6 text-slate-600">The main factors increasing this assessment's estimated exposure.</div>

                  {drivers.length ? (
                    <ul className="mt-5 grid grid-cols-1 gap-3.5 text-sm text-slate-700 md:grid-cols-2">
                      {drivers.map((driver) => (
                        <li key={driver} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                          <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-slate-900" />
                          <span>{driver}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                      No exposure driver summary is available for this snapshot.
                    </div>
                  )}
                </section>

                <HighExposureTrendChart points={trendPoints} symbol={heroMetrics?.symbol || "$"} entityLabel={entityName} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
